import { useCallback, useMemo } from "react";
import type {
  PaymentRequestStatus,
  PaymentRequestStatusDto,
} from "../entities/paymentRequestDefs";
import { usePaymentRequestComments } from "./usePaymentRequestComments";
import { usePaymentRequestStatusHistory } from "./usePaymentRequestStatusHistory";
import { usePrivateNotes } from "./usePrivateNotes";

interface StatusChange {
  type: "statusChange";
  from: PaymentRequestStatus;
  to: PaymentRequestStatus;
  creationTime: string | Date;
  id: string;
}

function getStatusChanges(statuses: PaymentRequestStatusDto[]): StatusChange[] {
  return [...statuses]
    .filter(
      (entry): entry is typeof entry & { creationTime: string } =>
        typeof entry.creationTime === "string"
    )
    .sort(
      (a, b) =>
        new Date(a.creationTime).getTime() - new Date(b.creationTime).getTime()
    )
    .reduce<StatusChange[]>(
      (acc, { id, status: to, creationTime }, index, arrayOfStatuses) => {
        if (index === 0) {
          return acc;
        }
        const from = arrayOfStatuses.at(index - 1)?.status;
        if (!from || !to || !creationTime || !id) {
          return acc;
        }

        acc.push({ from, to, creationTime, id, type: "statusChange" });
        return acc;
      },
      []
    );
}

export function usePaymentRequestMessages(direction?: "asc" | "desc") {
  const {
    items: statuses,
    isLoading: isStatusesLoading,
    mutate: mutatePaymentRequestStatusHistory,
  } = usePaymentRequestStatusHistory();
  const {
    items: commentsItems,
    isLoading: isCommentsLoading,
    mutate: mutateComments,
  } = usePaymentRequestComments();
  const comments = useMemo(
    () =>
      commentsItems?.map((comment) => ({
        type: "comment" as const,
        ...comment,
      })) || [],
    [commentsItems]
  );

  const { data: notesData } = usePrivateNotes();
  const notes = useMemo(
    () =>
      notesData?.items?.map((note) => ({ type: "note" as const, ...note })) ??
      [],
    [notesData]
  );
  const statusChanges = useMemo(
    () => getStatusChanges(statuses ?? []),
    [statuses]
  );
  const messages = useMemo(() => {
    return [...comments, ...notes, ...statusChanges].sort((a, b) => {
      if (!a.creationTime || !b.creationTime) {
        return 0;
      }
      if (a.creationTime && b.creationTime) {
        if (direction === "asc") {
          return new Date(a.creationTime) > new Date(b.creationTime) ? 1 : -1;
        }
        return new Date(a.creationTime) < new Date(b.creationTime) ? 1 : -1;
      }
      return 0;
    });
  }, [notes, comments, statusChanges, direction]);
  const isLoading = isStatusesLoading || isCommentsLoading;
  const revalidate = useCallback(
    () => Promise.all([mutatePaymentRequestStatusHistory(), mutateComments()]),
    [mutateComments, mutatePaymentRequestStatusHistory]
  );

  return { messages, isLoading, revalidate };
}
